import { QueryClient, onlineManager } from "@tanstack/react-query";
import { isMobileDevice, isPWA, isOnline } from "@utils/device";

/**
 * Smart refetch handler: debounces refetches on app resume and skips when offline.
 * Uses TanStack's onlineManager so behavior is consistent with ConnectivityMonitor.
 */
function createSmartRefetchHandler() {
  let lastRefetchTime = 0;

  return (_query: { state: { dataUpdatedAt?: number } }) => {
    const now = Date.now();
    const isMobile =
      typeof window !== "undefined" && (isMobileDevice() || isPWA());
    const DEBOUNCE_DELAY = isMobile ? 2000 : 1000;

    if (now - lastRefetchTime < DEBOUNCE_DELAY) return false;
    if (typeof window !== "undefined" && !onlineManager.isOnline())
      return false;
    if (!isOnline()) return false;

    lastRefetchTime = now;
    return true;
  };
}

const smartRefetchHandler = createSmartRefetchHandler();

/**
 * T3-style query client factory. Single place for default options.
 * - offlineFirst for all: use cached data when offline; refetch when back online (onlineManager).
 * - refetchOnMount: only when online, else avoid pointless network calls.
 * - retry: skip when offline to avoid toast/error spam.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes
        retry: (failureCount) => {
          if (typeof window !== "undefined" && !onlineManager.isOnline())
            return false;
          const max = typeof window !== "undefined" && isMobileDevice() ? 2 : 1;
          return failureCount < max;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // exponential backoff up to 30s
        refetchOnWindowFocus: (query) => smartRefetchHandler(query),
        refetchOnMount: (query) => {
          if (typeof window !== "undefined" && !onlineManager.isOnline())
            return false;
          if (query.state.dataUpdatedAt) {
            const timeSinceUpdate = Date.now() - query.state.dataUpdatedAt;
            const staleTime = 1000 * 60 * 5;
            return timeSinceUpdate > staleTime;
          }
          return true;
        },
        networkMode: "offlineFirst",
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

let clientQueryClientSingleton: QueryClient | undefined;

/**
 * T3-style accessor: server always gets a new client; browser reuses a singleton.
 * Use this in the provider so persistence (IndexedDB) attaches to the same client.
 */
export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") {
    return createQueryClient();
  }
  clientQueryClientSingleton ??= createQueryClient();
  return clientQueryClientSingleton;
}

/** For call sites that already import queryClient (e.g. sidebar sign-out). */
export const queryClient = getQueryClient();
